import React, { useEffect, useState, useContext } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import styles from "./index.module.css";
import MockCaseHeader from "./MockCaseHeader";
import MockCaseMain from "./MockCaseMain";
import type { InterviewCase } from "../CaseSelectionPage";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

import PermissionDialog from "./PermissionDialog";

// Import the tutorial components
import { TutorialProvider } from "./Tutorial/TutorialContext";
import TutorialOverlay from "./Tutorial/TutorialOverlay";
import { useAuth } from "src/contexts/AuthContext";
import { useMediaPermission } from "src/contexts/MediaPermissionContext";
import { CameraProvider } from "src/contexts/CameraContext";

// Create a wrapper component that uses the tutorial context
const MockCaseContent: React.FC<{
    interviewCase: InterviewCase;
    sessionId: string;
    isFirstTimeUser: boolean;
}> = ({ interviewCase, sessionId, isFirstTimeUser }) => {
    const location = useLocation();
    const { permissions, errors, hasRequiredPermissions, requestPermissions } =
        useMediaPermission();
    const [showPermissionDialog, setShowPermissionDialog] = useState(true);

    // Check if tutorial should start automatically from route state
    useEffect(() => {
        const { startTutorial: shouldStartTutorial } = location.state || {};
        // The tutorial will auto-start based on isFirstTimeUser in the TutorialProvider
    }, [location.state, isFirstTimeUser]);

    const handleRequestPermissions = async () => {
        // First, check if we can access the MediaDevices API
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error("MediaDevices interface not available");
            return;
        }

        // Check current permission state
        const checkPermissions = async () => {
            try {
                // Query current permission state if available
                if (navigator.permissions && navigator.permissions.query) {
                    const result = await navigator.permissions.query({
                        name: "microphone" as PermissionName,
                    });
                    if (result.state === "denied") {
                        console.error(
                            "Microphone permission is denied in browser settings"
                        );
                        return false;
                    }
                }
                return true;
            } catch (e) {
                // If we can't query permissions, proceed with getUserMedia
                return true;
            }
        };

        const canRequest = await checkPermissions();

        if (!canRequest) {
            return;
        }

        try {
            // Request both permissions
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: true,
            });

            // Immediately stop the stream
            stream.getTracks().forEach((track) => track.stop());
            window.location.reload();
        } catch (error) {
            // If getting both fails, try audio only
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false,
                });

                // Immediately stop the stream
                audioStream.getTracks().forEach((track) => track.stop());
                window.location.reload();
            } catch (audioError) {
                if (audioError instanceof DOMException) {
                    switch (audioError.name) {
                        case "NotAllowedError":
                            if (
                                audioError.message.includes(
                                    "Permission dismissed"
                                )
                            ) {
                                console.error(
                                    "Permission prompt was dismissed. Please try again."
                                );
                            } else {
                                console.error(
                                    "Permission was denied. Please enable microphone access in your browser settings."
                                );
                            }
                            break;
                        case "NotFoundError":
                            console.error(
                                "No microphone detected. Please connect a microphone and try again."
                            );
                            break;
                        case "NotReadableError":
                            console.error(
                                "Could not access your microphone. Please check if another application is using it."
                            );
                            break;
                        default:
                            console.error(
                                "Error accessing microphone:",
                                audioError.message
                            );
                    }
                } else {
                    console.error(
                        "Unknown error occurred while accessing microphone"
                    );
                }
            }
        }
    };

    // Tutorial will only be active if needed
    return (
        <>
            <PermissionDialog
                open={showPermissionDialog && !hasRequiredPermissions}
                permissions={permissions}
                errors={errors}
                onRequestPermissions={requestPermissions}
            />

            <div className={styles.appContainer}>
                <MockCaseHeader
                    title={interviewCase.title}
                    participantCount={2}
                />
                <MockCaseMain />

                {/* Tutorial overlay will only be visible when active */}
                {isFirstTimeUser && <TutorialOverlay />}
            </div>
        </>
    );
};

// Error message component
const ErrorMessage: React.FC<{ countdown: number; navigate: any }> = ({
    countdown,
    navigate,
}) => {
    const handleGoHome = () => {
        navigate("/");
        window.location.reload();
    };

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
            <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-red-600 mb-4">
                    Error Loading Interview
                </h2>
                <p className="text-gray-600 mb-4">
                    Unable to load the interview data. This might happen if you:
                </p>
                <ul className="text-left text-gray-600 mb-6 list-disc pl-6">
                    <li>
                        <strong>Refreshed the page</strong> during an interview
                    </li>
                    <li>
                        <strong>Accessed this page directly</strong> without
                        selecting a case
                    </li>
                    <li>
                        Used an <strong>invalid or expired</strong> link
                    </li>
                </ul>
                <p className="text-gray-600 mb-6">
                    Redirecting to home in{" "}
                    <span className="font-bold text-blue-600">{countdown}</span>{" "}
                    seconds...
                </p>
                <button
                    onClick={handleGoHome}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    Go back to home
                </button>
            </div>
        </div>
    );
};

// Main component with TutorialProvider
const MockCasePage: React.FC = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = usePartnerAwareNavigate();
    const sessionId = searchParams.get("interviewSessionId");
    const { userDoc } = useAuth();
    const [redirectCountdown, setRedirectCountdown] = useState(5);
    const [shouldRedirect, setShouldRedirect] = useState(false);

    useEffect(() => {
        // Check if we need to navigate after refresh
        const shouldNavigateHome = sessionStorage.getItem("navigateHome");
        if (shouldNavigateHome) {
            sessionStorage.removeItem("navigateHome");
            navigate("/");
        }
    }, [navigate]);

    // Handle redirection when location.state is null
    useEffect(() => {
        if (!location.state) {
            setShouldRedirect(true);
            const timer = setInterval(() => {
                setRedirectCountdown((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [location.state]);

    // Handle the actual navigation
    useEffect(() => {
        if (shouldRedirect && redirectCountdown <= 0) {
            sessionStorage.setItem("navigateHome", "true");
            window.location.reload();
        }
    }, [shouldRedirect, redirectCountdown]);

    // Show error message if location.state is null
    if (!location.state) {
        return (
            <ErrorMessage countdown={redirectCountdown} navigate={navigate} />
        );
    }

    const { interviewCase } = location.state as {
        interviewCase: InterviewCase | null;
    };

    console.log("interviewCase", interviewCase);

    // In case someone navigates directly to /mock-case without a state
    if (!interviewCase || !sessionId) {
        return <div>No case data provided. Please select a case first.</div>;
    }

    return (
        <CameraProvider>
            <TutorialProvider isFirstTimeUser={!!userDoc?.isFirstTimeUser}>
                <MockCaseContent
                    interviewCase={interviewCase}
                    sessionId={sessionId}
                    isFirstTimeUser={!!userDoc?.isFirstTimeUser}
                />
            </TutorialProvider>
        </CameraProvider>
    );
};

export default MockCasePage;
